/**
 * Authentication Composable
 * 
 * Provides reactive access to authentication state and actions.
 * 
 * @example
 * ```typescript
 * import { useAuth } from '@/composables/core/useAuth';
 * 
 * export default defineComponent({
 *   setup() {
 *     const { isAuthenticated, userId, login, logout } = useAuth();
 *     
 *     const handleLogin = async () => {
 *       await login({ email: 'user@example.com', password: 'pass' });
 *     };
 *     
 *     return { isAuthenticated, userId, handleLogin, logout };
 *   }
 * });
 * ```
 */

import { computed } from 'vue';
import { useStore } from './useStore';

interface LoginCredentials {
  email: string;
  password: string;
}

export function useAuth() {
  const store = useStore();

  // Reactive computed properties
  const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
  const userId = computed(() => store.getters['auth/userId']);
  const token = computed(() => store.getters['auth/token']);
  const email = computed(() => store.getters['auth/userEmail']);
  const userName = computed(() => store.getters['auth/userName']);
  const userLastName = computed(() => store.getters['auth/userLastName']);

  // Actions
  const login = async (credentials: LoginCredentials) => {
    await store.dispatch('auth/login', credentials);
  };

  const logout = async () => {
    await store.dispatch('auth/logout');
  };

  const signup = async (data: any) => {
    await store.dispatch('auth/signup', data);
  };

  return {
    // State
    isAuthenticated,
    userId,
    token,
    email,
    userName,
    userLastName,
    
    // Actions
    login,
    logout,
    signup
  };
}

